import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, LayersControl, Pane, GeoJSON, Polyline, useMapEvent } from 'react-leaflet';
import { Map, LatLngBoundsExpression, latLngBounds } from 'leaflet';
import { LandingPadsLayer } from './LandingPadsLayer';
import { RoutePoint, RoutePointType } from '../RouteConfirmation';
import { v4 as uuidv4 } from 'uuid';
import { RouteArrowMarkers } from './RouteArrowMarkers';
import { geofenceColor, indexPriority } from 'utils/ui';
import { useAppSelector } from 'hooks/redux';
import { selectCellGeofences } from 'reducers/geofencesReducer';
import { distToSegment } from 'views/RoutePlanner/util';
import RouteConfirmationGraph from './RouteConfirmationGraph';
import { parseFlightRoutes } from 'views/DevTools/tools';
import useInterval from 'utils/useInterval';
import { selectFilteredOrders, selectSelectedCellId, selectLandingZones } from 'selectors/ordersSelector';
import { h3SetToFeature } from 'geojson2h3';
import { Order, PlaceContext, LandingPad } from '@m4a3/api-client';
import { fetchAirspace, updateRoute, reserveRoute, deleteMissionAirspace } from 'services/airspaceApi';
import { Button, CircularProgress, Alert } from '@mui/material';
import { orderStatuses } from 'constants/orderConstants';
import { getObstacles } from 'actions/obstacleActions';
import { getElevationHazards } from 'actions/elevationHazardsActions';
import { useDispatch, useSelector } from 'react-redux';
import { newNotification } from 'actions/uiActions';
import { selectObstacles } from 'reducers/obstaclesReducer';
import { selectElevationHazards } from 'selectors/elevationHazardsSelectors';
import Obstacles from './Obstacles';
import ElevationHazards from './ElevationHazards';
import Typography from '@mui/material/Typography';
import InfoRow from 'components/InfoRow';
import assignPadActionIcon from '../../../assets/svgs/assignPadActionIcon.svg';
import confirmRouteIcon from '../../../assets/svgs/confirmRoute.svg';

import orderDetailsEircodeIcon from '../../../assets/svgs/orderDetailsEircodeIcon.svg';

import OrdersRoutesLayer from './OrdersRoutesLayer';
import { rerouteOrderThunk } from 'actions/adminActions';
import { getShortIdForOrder, renderOrderKm } from '../../../utils/orderUtils';
import { getDistanceBetweenPoints } from 'utils/parsingUtils';
import ActionConfirmationModal from './ActionConfirmationModal';
import clearWaypointIcon from '../../../assets/svgs/ClearWaypoint.svg';
import revertIcon from '../../../assets/svgs/Revert.svg';
import deleteBinIcon from '../../../assets/svgs/DeleteBin.svg';
import { logToDatadog } from 'services/datadog';

const { BaseLayer } = LayersControl;


/** ---------- Updating our Route Coords happens below START ---------- */

interface NewRouteConfirmationMapProps {
  intialCoords: [number, number];
  outerMapBounds?: LatLngBoundsExpression;
  mapRef?: React.MutableRefObject<Map>;
  zoom?: number;
  children?: React.ReactNode;
  landingZoneId: string;
  initialRouteCoords: RoutePoint[];
  overlays?: React.ReactNode;
  geoJson?: any;
  order?: Order;
  missionRoutes?: any;
  geoTunnel?: any;
  placeContext?: PlaceContext;
  onUpdateRouteSuccess?: () => void;
  onUpdateRouteError?: (error: any) => void;
  onReserveRouteSuccess?: (updatedOrder: Order) => void;
  onReserveRouteError?: (error: any) => void;
  originLandingPad?: LandingPad;
  destinationLandingPad?: LandingPad;
  onDeleteMissionAirspaceSuccess?: () => void;
  isSymmetricalRoute?: boolean;
}

export const NewRouteConfirmationMap = ({
  intialCoords,
  outerMapBounds,
  mapRef,
  children,
  landingZoneId,
  initialRouteCoords,
  overlays,
  geoJson,
  order,
  missionRoutes: missionRoutesProp,
  geoTunnel,
  placeContext,
  onUpdateRouteSuccess,
  onUpdateRouteError,
  onReserveRouteSuccess,
  onReserveRouteError,
  originLandingPad,
  destinationLandingPad,
  onDeleteMissionAirspaceSuccess,
  isSymmetricalRoute = false
}: NewRouteConfirmationMapProps) => {
  const [changedRouteCoords, setChangedRouteCoords] = useState<RoutePoint[]>(initialRouteCoords);
  const initialRouteCoordsRef = useRef<RoutePoint[]>(initialRouteCoords);
  const geofences = useAppSelector(selectCellGeofences);
  const [updated, setUpdated] = useState<string>(uuidv4());
  const changedRouteCoordsRef = useRef<RoutePoint[]>(initialRouteCoords);
  const [missionRoutes, setMissionRoutes] = useState<any>({});
  const routesToRender = missionRoutesProp ?? missionRoutes;
  const memoizedMissionRoutes = useMemo(() => routesToRender, [JSON.stringify(routesToRender)]);
  const selectedCellId = useAppSelector(selectSelectedCellId);
  const obstacles = useAppSelector(selectObstacles);
  const elevationHazards = useAppSelector(selectElevationHazards);
  const dispatch = useDispatch();
  const [isUpdatingRoute, setIsUpdatingRoute] = useState<boolean>(false);
  const [isReservingRoute, setIsReservingRoute] = useState<boolean>(false);
  const [isDeletingAirspace, setIsDeletingAirspace] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [isRouteReserved, setIsRouteReserved] = useState<boolean>(false);
  const orders = useSelector(selectFilteredOrders);
  const landingZones = useSelector(selectLandingZones);
  console.log('orders', orders);
  const [orderIdDropdownOpen, setOrderIdDropdownOpen] = useState<boolean>(false);
  const handleOrderIdClick = useCallback(() => setOrderIdDropdownOpen(prev => !prev), []);
  const shortOrderId = order ? getShortIdForOrder(order as any) : '';

  // Track the last bounds we applied to avoid repeated fitBounds loops that cause flicker
  const lastAppliedBoundsRef = useRef<any>(null);


  useInterval(
    () => {
      if (missionRoutesProp) { return; }
      fetchAirspace(selectedCellId).then(res => {
        const missions: any = parseFlightRoutes(res.data);
        setMissionRoutes(missions);
        console.log('missionRoutes', res);
      });
    },
    5000
  );

  useEffect(() => {
    dispatch(getObstacles(selectedCellId, true));
  }, [selectedCellId]);

  useEffect(() => {
    if (originLandingPad?.id) {
      dispatch(getElevationHazards(`${originLandingPad.id}`));
    }
  }, [originLandingPad?.id, dispatch]);

  // moved below addMarker definition to avoid use-before-define

  const hasPreferredDropPoint = useMemo(() => {
    const preferredId = placeContext?.place?.preferredDropPointId;
    if (!preferredId) { return false; }
    const dropPoints = placeContext?.dropPoints?.data ?? [];
    return Array.isArray(dropPoints) && dropPoints.some((d: any) => d?.id === preferredId && !d?.isDeleted);
  }, [placeContext]);

  const editingLockedDueToMissingPreferredDrop = useMemo(() => {
    return Boolean(originLandingPad) && !hasPreferredDropPoint;
  }, [originLandingPad, hasPreferredDropPoint]);

  const isEditingLocked = useMemo(() => {
    const noInitial = !(initialRouteCoords && initialRouteCoords.length > 0);
    const noOrigin = !originLandingPad;
    const missionIsHold = order?.mission?.status === 'HOLD';
    const missingPreferred = editingLockedDueToMissingPreferredDrop;
    return isRouteReserved || (noInitial && noOrigin) || missionIsHold || missingPreferred;
  }, [initialRouteCoords, originLandingPad, isRouteReserved, order?.mission?.status, editingLockedDueToMissingPreferredDrop]);

  // Symmetry helpers
  const findDropIndex = useCallback((coords: RoutePoint[]): number => {
    return coords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
  }, []);

  const getMirrorIndexForNode = useCallback((idx: number, coords: RoutePoint[]): number | null => {
    const d = findDropIndex(coords);
    if (d === -1) { return null; }
    if (idx === d) { return null; }
    if (idx < d) {
      return d + (d - idx);
    }
    if (idx > d) {
      return d - (idx - d);
    }
    return null;
  }, [findDropIndex]);

  const getMirrorInsertionIndex = useCallback((insertAt: number, coords: RoutePoint[]): number | null => {
    const d = findDropIndex(coords);
    if (d === -1) { return null; }
    if (insertAt <= d) {
      // Inserting on outbound between [insertAt-1, insertAt]
      // Mirror insertion should be AFTER mirror of the 'to' node (insertAt)
      const mirrorOfTo = getMirrorIndexForNode(insertAt, coords);
      if (mirrorOfTo === null) {
        // If 'to' is the drop itself, mirror segment starts at drop on inbound
        return d + 1;
      }
      return mirrorOfTo + 1;
    } else {
      // Inserting on inbound between [insertAt-1, insertAt]
      // Mirror insertion should be AFTER mirror of the 'to' node (insertAt)
      const mirrorOfTo = getMirrorIndexForNode(insertAt, coords);
      return mirrorOfTo === null ? null : (mirrorOfTo + 1);
    }
  }, [findDropIndex, getMirrorIndexForNode]);

  // Normalize place geometry to a GeoJSON Feature/FeatureCollection for rendering
  const placeBoundaryGeoJson = useMemo(() => {
    try {
      const rawGeometry = placeContext?.place?.geometry as any;
      if (!rawGeometry) { return null; }
      const parsed = typeof rawGeometry === 'string' ? JSON.parse(rawGeometry) : rawGeometry;
      if (!parsed || !parsed.type) { return null; }
      if (parsed.type === 'Feature' || parsed.type === 'FeatureCollection') { return parsed; }
      return { type: 'Feature', geometry: parsed, properties: {} } as any;
    } catch {
      return null;
    }
  }, [placeContext]);

  // Fallback route builder when no initial route is provided
  const computeFallbackRoute = useCallback((): RoutePoint[] | undefined => {
    if (!originLandingPad || !destinationLandingPad || !placeContext) { return undefined; }
    const preferredId = placeContext?.place?.preferredDropPointId;
    const dropPoint = placeContext?.dropPoints?.data?.find(d => d.id === preferredId);
    if (!dropPoint) { return undefined; }

    const route: RoutePoint[] = [
      {
        coordinates: [originLandingPad.latitude, originLandingPad.longitude],
        type: RoutePointType.START_POINT_BASE,
        index: 0
      },
      {
        coordinates: [dropPoint.latitude, dropPoint.longitude],
        type: RoutePointType.CUSTOMER_DROP_LOCATION,
        index: 1
      },
      {
        coordinates: [destinationLandingPad.latitude, destinationLandingPad.longitude],
        type: RoutePointType.END_POINT_BASE,
        index: 2
      }
    ];

    return route;
  }, [originLandingPad, destinationLandingPad, placeContext]);

  // Keep local state in sync when parent provides route coords asynchronously, with fallback
  useEffect(() => {
    const hasInitial = (initialRouteCoords || []).length > 0;
    const effective = hasInitial ? initialRouteCoords : computeFallbackRoute();
    setChangedRouteCoords((effective || []).map(point => ({ ...point })));
  }, [initialRouteCoords, computeFallbackRoute]);

  useEffect(() => {
    console.log('changedRouteCoords', changedRouteCoords);
  }, [changedRouteCoords]);

  // Keep a ref of latest initial route coords to support Reset using freshest props, with fallback
  useEffect(() => {
    const hasInitial = (initialRouteCoords || []).length > 0;
    const effective = hasInitial ? initialRouteCoords : computeFallbackRoute();
    initialRouteCoordsRef.current = effective || [];
    setMapBounds(effective);
  }, [initialRouteCoords, computeFallbackRoute]);

  // Keep a ref of latest route coords to avoid stale closures in Leaflet handlers
  useEffect(() => {
    changedRouteCoordsRef.current = changedRouteCoords || [];
  }, [changedRouteCoords]);

  // Memoized route distance calculation for performance
  const routeDistanceKm = useMemo(() => {
    const coords = changedRouteCoords || [];
    if (coords.length < 2) { return 0; }

    let totalDistance = 0;
    for (let i = 0; i < coords.length - 1; i++) {
      const [lat1, lng1] = coords[i].coordinates;
      const [lat2, lng2] = coords[i + 1].coordinates;
      const segmentDistance = getDistanceBetweenPoints(lat1, lng1, lat2, lng2);
      totalDistance += segmentDistance; // meters
    }

    return Math.ceil((totalDistance / 1000) * 10) / 10; // km, round up to 1 dp
  }, [changedRouteCoords]);

  const callUpdateRoute = async () => {
    setIsUpdatingRoute(true);
    let newRouteCoords = [...changedRouteCoords];
    if (isSymmetricalRoute) {
      // Get route up to drop point
      const dropPointIndex = newRouteCoords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
      if (dropPointIndex !== -1) {
        // Take route up to drop point
        const routeToDropPoint = newRouteCoords.slice(0, dropPointIndex + 1);
        // Create reversed route from drop point back to start, excluding the drop point itself
        const reversedRoute = [...routeToDropPoint.slice(0, -1)].reverse();
        // Combine routes to create full route
        newRouteCoords = [...routeToDropPoint, ...reversedRoute];
      }
    }

    try {
      await updateRoute(order.id, order.missionId, newRouteCoords);
      setIsRouteReserved(true);
      onUpdateRouteSuccess && onUpdateRouteSuccess();
    } catch (error) {
      onUpdateRouteError && onUpdateRouteError(error);
    } finally {
      setIsUpdatingRoute(false);
    }
  }


  const callReserveRoute = async () => {
    setIsReservingRoute(true);
    try {
      const updatedOrder = await reserveRoute(order.id, order.missionId);
      setIsRouteReserved(true);
      onReserveRouteSuccess && onReserveRouteSuccess(updatedOrder as any);
    } catch (error) {
      onReserveRouteError && onReserveRouteError(error);
    } finally {
      setIsReservingRoute(false);
    }
  }

  const setMapBounds = routeCoords => {
    if (!routeCoords || !routeCoords.length) { return }
    console.log('setMapBounds', routeCoords);
    const bounds = latLngBounds([]);

    routeCoords.forEach(coord => {
      bounds.extend(coord?.coordinates ?? coord);
    });

    // setOuterMapBounds(bounds as any);

    if (!mapRef?.current) { return; }

    // Avoid re-applying the same bounds repeatedly
    if (lastAppliedBoundsRef.current && lastAppliedBoundsRef.current.equals(bounds)) {
      return;
    }

    mapRef.current.fitBounds(bounds as any, { animate: false } as any);
    lastAppliedBoundsRef.current = bounds;
  };

  // If outerMapBounds is provided by the parent, apply it imperatively (not via prop)
  // to prevent React creating a new bounds object each render and thrashing the map.
  useEffect(() => {
    if (!mapRef?.current || !outerMapBounds) { return; }
    const newBounds = latLngBounds(outerMapBounds as any);

    if (lastAppliedBoundsRef.current && lastAppliedBoundsRef.current.equals(newBounds)) {
      return;
    }

    mapRef.current.fitBounds(newBounds as any, { animate: false } as any);
    lastAppliedBoundsRef.current = newBounds;
  }, [outerMapBounds, mapRef]);

  const callDeleteMissionAirspace = async () => {
    if (!order?.missionId) {
      dispatch(newNotification({ variant: 'error', message: 'There is no mission id for this order' }));
      return;
    }
    setIsDeletingAirspace(true);
    try {
      await deleteMissionAirspace(order.id as any, order.missionId as any);
      dispatch(newNotification({ variant: 'success', message: 'Deleted mission airspace' }));
      await dispatch(rerouteOrderThunk(order.id as any));
      // Inform parent to close the modal after successful deletion
      if (onDeleteMissionAirspaceSuccess) {
        onDeleteMissionAirspaceSuccess();
      }
      // Refresh local airspace view if we manage it here
      if (!missionRoutesProp) {
        const res = await fetchAirspace(selectedCellId);
        const missions: any = parseFlightRoutes(res.data);
        setMissionRoutes(missions);
      }
    } catch (err) {
      dispatch(newNotification({ variant: 'error', message: 'Failed to delete mission airspace' }));
    } finally {
      setIsDeletingAirspace(false);
    }
  };

  const renderAirspaceActions = () => {
    const showEditButtons = !isEditingLocked && (order.mission?.status !== 'HOLD');
    const hasWaypoints = (changedRouteCoords || []).some(point => point.type === RoutePointType.WAYPOINT);

    const initial = initialRouteCoordsRef.current || [];
    const changed = changedRouteCoords || [];
    const routeHasChanged =
      initial.length !== changed.length ||
      initial.some((point, idx) => {
        const [ilat, ilng] = point.coordinates;
        const other = changed[idx];
        if (!other) { return true; }
        const [clat, clng] = other.coordinates;
        return ilat !== clat || ilng !== clng;
      });

    const hideReserveRoute =
      order.mission?.status === 'HOLD' ||
      order.mission?.status === 'COMPLETED' ||
      order.mission?.status === 'IN_PROGRESS';

    const reserveDisabled = isUpdatingRoute || isReservingRoute;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!hideReserveRoute && (
          <div
            onClick={() => {
              if (reserveDisabled || isRouteReserved) { return; }
              if (routeHasChanged) { callUpdateRoute(); } else { callReserveRoute(); }
            }}
            style={{
              background: isRouteReserved ? '#F3F4F6' : (reserveDisabled ? '#7DAEF7' : '#0583FF'),
              borderRadius: 12,
              padding: '12px 16px',
              cursor: isRouteReserved ? 'default' : (reserveDisabled ? 'not-allowed' : 'pointer'),
              opacity: isRouteReserved ? 1 : (reserveDisabled ? 0.8 : 1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pointerEvents: isRouteReserved ? 'none' : 'auto'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 64, height: 64, borderRadius: 32, background: isRouteReserved ? '#16A34A' : '#0B6EFD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {isRouteReserved ? (
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 13L9 17L19 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.5078 13H20.9422C23.9895 13 25.5131 13 26.0914 13.5473C26.5914 14.0204 26.813 14.7173 26.678 15.3922C26.5218 16.173 25.2779 17.0529 22.7901 18.8125L18.7255 21.6875C16.2377 23.4471 14.9938 24.327 14.8376 25.1078C14.7027 25.7827 14.9242 26.4796 15.4242 26.9527C16.0025 27.5 17.5262 27.5 20.5734 27.5H21.5078M17.0078 13C17.0078 14.6569 15.6647 16 14.0078 16C12.351 16 11.0078 14.6569 11.0078 13C11.0078 11.3431 12.351 10 14.0078 10C15.6647 10 17.0078 11.3431 17.0078 13ZM31.0078 27C31.0078 28.6569 29.6647 30 28.0078 30C26.351 30 25.0078 28.6569 25.0078 27C25.0078 25.3431 26.351 24 28.0078 24C29.6647 24 31.0078 25.3431 31.0078 27Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ color: isRouteReserved ? '#111827' : 'white', fontWeight: 600, fontSize: 20 }}>
                {isRouteReserved ? 'Route Reserved' : (routeHasChanged ? 'Update & Reserve Route' : 'Reserve Route')}
              </div>
            </div>
            {reserveDisabled ? (
              <CircularProgress size={16} style={{ color: 'white' }} />
            ) : isRouteReserved ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13L9 17L19 7" stroke="#16A34A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 6L15 12L9 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px 16px',
            marginTop: 4
          }}
        >
          {/* Revert */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px',
              cursor: showEditButtons ? 'pointer' : 'not-allowed',
              borderRadius: 8,
              transition: 'background-color 0.2s ease',
              opacity: showEditButtons ? 1 : 0.5
            }}
            onClick={() => { if (showEditButtons) { resetRouteCoords(); } }}
            onMouseEnter={(e) => { if (showEditButtons) { e.currentTarget.style.backgroundColor = '#f8fafc'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <img src={revertIcon} alt="Revert" style={{ width: 36, height: 36 }} />
            </div>
            <Typography variant="body2" style={{ fontSize: 16, fontWeight:400, textAlign: 'center' }}>
              Revert
            </Typography>
          </div>

          {/* Clear Waypoints */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px',
              cursor: showEditButtons && hasWaypoints ? 'pointer' : 'not-allowed',
              borderRadius: 8,
              transition: 'background-color 0.2s ease',
              opacity: showEditButtons && hasWaypoints ? 1 : 0.5
            }}
            onClick={() => { if (showEditButtons && hasWaypoints) { clearWaypoints(); } }}
            onMouseEnter={(e) => { if (showEditButtons && hasWaypoints) { e.currentTarget.style.backgroundColor = '#f8fafc'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <img src={clearWaypointIcon} alt="Clear waypoints" style={{ width: 36, height: 36 }} />
            </div>
            <Typography variant="body2" style={{ fontSize: 16, fontWeight:400, textAlign: 'center' }}>
              Clear
              <br />
              Waypoints
            </Typography>
          </div>

          {/* Delete Mission */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '12px',
              cursor: isDeletingAirspace ? 'not-allowed' : 'pointer',
              borderRadius: 8,
              transition: 'background-color 0.2s ease'
            }}
            onClick={() => { if (!isDeletingAirspace) { setDeleteModalOpen(true); } }}
            onMouseEnter={(e) => { if (!isDeletingAirspace) { e.currentTarget.style.backgroundColor = '#f8fafc'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <div style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: 'rgba(230,26,39,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              {isDeletingAirspace ? (
                <CircularProgress size={20} />
              ) : (
                <img src={deleteBinIcon} alt="Delete mission" style={{ width: 36, height: 36 }} />
              )}
            </div>
            <Typography variant="body2" style={{ fontSize: 16, fontWeight:400, textAlign: 'center', color: '#1F2937' }}>
              Delete
              <br />
              Mission
            </Typography>
          </div>
        </div>
      </div>
    );
  }

  const addMarker = useCallback((evt: any) => {
    if (isEditingLocked) {
      dispatch(newNotification({ variant: 'error', message: 'Editing disabled while route is reserved or on hold.' }));
      return;
    }
    if (!mapRef.current) { return }

    const currentCoords = changedRouteCoordsRef.current || [];
    const coordsToAdd: RoutePoint = {
      coordinates: [evt.latlng.lat, evt.latlng.lng],
      type: RoutePointType.WAYPOINT,
      index: currentCoords.length
    };

    let newRouteCoords = currentCoords.map(point => ({ ...point }));

    if (currentCoords.length > 1) {

      const distanceFromSegments = newRouteCoords
        .map((point, i, arr) => {
          if (i === 0) return null as unknown as number; // filtered out below
          const dist = distToSegment(
            coordsToAdd.coordinates,
            arr[i - 1].coordinates,
            point.coordinates
          );
          return dist;
        })
        .filter(Boolean) as number[];

      const closestSegment = Math.min(...distanceFromSegments);
      const segIndex = distanceFromSegments.indexOf(closestSegment);

      const insertAt = segIndex + 1;
      newRouteCoords.splice(insertAt, 0, coordsToAdd);

      // If route is symmetrical, also insert mirrored waypoint
      if (isSymmetricalRoute) {
        const mirrorInsert = getMirrorInsertionIndex(insertAt, currentCoords);
        if (mirrorInsert !== null) {
          const adjustedMirrorInsert = insertAt <= mirrorInsert ? mirrorInsert + 1 : mirrorInsert;
          const mirrorPoint: RoutePoint = {
            coordinates: [...coordsToAdd.coordinates] as [number, number],
            type: RoutePointType.WAYPOINT,
            index: 0
          } as any;
          newRouteCoords.splice(adjustedMirrorInsert, 0, mirrorPoint);
        }
      }
    } else {
      newRouteCoords.push(coordsToAdd);
    }
    // Reindex using new objects to avoid mutating shared references
    newRouteCoords = newRouteCoords.map((point, idx) => ({ ...point, index: idx }));
    setChangedRouteCoords(newRouteCoords);

    setUpdated(uuidv4());
  }, [mapRef, isEditingLocked, getMirrorInsertionIndex, dispatch, order]);

  const MapClickHandler = () => {
    useMapEvent('click', addMarker);
    return null;
  };



  // extracted to RouteArrowMarkers component

  const removeMarker = (e: any, routeCoordIndex: number) => {
    if (order?.mission?.status === 'HOLD') { return; }
    // Only allow deletion of waypoints
    const point = changedRouteCoordsRef.current?.[routeCoordIndex];
    if (!point || point.type !== RoutePointType.WAYPOINT) { return; }
    setChangedRouteCoords(prevCoords => {
      let newRouteCoords: RoutePoint[] = [...prevCoords];

      if (isSymmetricalRoute) {
        // Remove the mirrored waypoint as well
        const mirrorIdx = getMirrorIndexForNode(routeCoordIndex, prevCoords);
        if (mirrorIdx !== null) {
          const toRemove = [routeCoordIndex, mirrorIdx].sort((a, b) => b - a);
          toRemove.forEach(idx => {
            const p = newRouteCoords[idx];
            if (p && p.type === RoutePointType.WAYPOINT) {
              newRouteCoords.splice(idx, 1);
            }
          });
        } else {
          newRouteCoords.splice(routeCoordIndex, 1);
        }
      } else {
        newRouteCoords.splice(routeCoordIndex, 1);
      }

      return newRouteCoords.map((p, idx) => ({ ...p, index: idx }));
    });
    setUpdated(uuidv4());
  }

  const updateMarker = (e: any, routeCoordIndex: number) => {
    const latLng = e.target.getLatLng();
    const newCoordsTuple: [number, number] = [latLng.lat, latLng.lng];
    setChangedRouteCoords(prev => {
      const mirrorIdx = isSymmetricalRoute ? getMirrorIndexForNode(routeCoordIndex, prev) : null;
      return prev.map((coord, index) => {
        if (index === routeCoordIndex) {
          return { ...coord, coordinates: newCoordsTuple };
        }
        if (mirrorIdx !== null && index === mirrorIdx) {
          return { ...coord, coordinates: newCoordsTuple };
        }
        return coord;
      });
    });
  }

  const getOutboundRouteCoords = (intialCoords: RoutePoint[]) => {
    const dropIndex = intialCoords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
    return intialCoords.slice(0, dropIndex + 1);
  }

  const getInboundRouteCoords = (intialCoords: RoutePoint[]) => {
    const dropIndex = intialCoords.findIndex(coord => coord.type === RoutePointType.CUSTOMER_DROP_LOCATION);
    return intialCoords.slice(dropIndex);
  }

  const resetRouteCoords = useCallback(() => {
    setChangedRouteCoords((initialRouteCoordsRef.current || []).map(point => ({ ...point })));
    setUpdated(uuidv4());
  }, []);

  const clearWaypoints = useCallback(() => {
    setChangedRouteCoords(prevCoords => {
      const filtered = (prevCoords || []).filter(point => point.type !== RoutePointType.WAYPOINT);
      return filtered.map((point, idx) => ({ ...point, index: idx }));
    });
    setUpdated(uuidv4());
  }, []);

  

  const renderUpdateOrReserveRoute = () => {
    if (order?.mission?.status === 'HOLD') {
      return null;
    }
    const initial = initialRouteCoordsRef.current || [];
    const changed = changedRouteCoords || [];
    const routeHasChanged =
      initial.length !== changed.length ||
      initial.some((point, idx) => {
        const [ilat, ilng] = point.coordinates;
        const other = changed[idx];
        if (!other) { return true; }
        const [clat, clng] = other.coordinates;
        return ilat !== clat || ilng !== clng;
      });
    if (routeHasChanged) {
      return (
        <Button
          color="primary"
          variant="contained"
          onClick={callUpdateRoute}
          disabled={isUpdatingRoute}
          endIcon={isUpdatingRoute ? <CircularProgress size={16} /> : null}
        >
          Update &amp; Reserve Route
        </Button>
      )
    }

    const hideReserveRoute =
      order.mission?.status === 'HOLD' ||
      order.mission?.status === 'COMPLETED' ||
      order.mission?.status === 'IN_PROGRESS';
    if (hideReserveRoute) {
      return null;
    }
    return (
      <Button
        color="primary"
        variant="contained"
        onClick={callReserveRoute}
        disabled={isReservingRoute}
        endIcon={isReservingRoute ? <CircularProgress size={16} /> : null}
      >
        Reserve Route
      </Button>
    )
  }


  return (
    <>
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <MapContainer
          style={{
            height: '100%',
            width: '100%'
          }}
          center={intialCoords}
          ref={mapRef}
          whenReady={() => {
            const mapInstance = mapRef?.current as any;
            if (!mapInstance) { return; }
            mapInstance.selectArea?.enable?.();
            try { mapInstance.attributionControl.remove(); } catch { }
          }}
          zoom={14}>
          {!isEditingLocked && <MapClickHandler />}
          <TileLayer
            attribution='&amp;copy <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <TileLayer
            attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          <LayersControl position="topleft">
            <BaseLayer checked name="Manna v1">
              <TileLayer
                attribution="Manna-v1"
                url="https://tiles.manna.aero/{z}/{x}/{y}.png"
                maxZoom={24}
              />
            </BaseLayer>
            <BaseLayer name="Manna v2">
              <TileLayer
                attribution="Manna-v2"
                url="https://tiles-dev.manna.aero/{z}/{x}/{y}.png"
                maxZoom={24}
              />
            </BaseLayer>
            <BaseLayer name="OpenStreetMap">
              <TileLayer
                attribution='&amp;copy <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </BaseLayer>
          </LayersControl>
          {children}
          {/* Landing zones geojson */}
          {landingZones?.map(lz => {
            let parsedGeoJson: any;
            try {
              parsedGeoJson = JSON.parse(lz.geoJson);
            } catch (error) {
              logToDatadog('error_parsing_geodata', { landingZone: lz.id }, 'error');
              return null;
            }

            return (
              <Pane name={`landing-zone-${lz.id}`} key={`landing-zone-${lz.id}`}>
                <GeoJSON
                  data={parsedGeoJson}
                  style={{
                    weight: 2,
                    fillOpacity: 0.1,
                    color: 'lightblue'
                  }}
                />
              </Pane>
            );
          })}
          <Pane name="polysuggested">
            {geoTunnel && (
              <GeoJSON
                data={geoTunnel}
                key={`${order?.id}-geoTunnel`}
                style={{
                  weight: 5,
                  fillOpacity: 0.5,
                  stroke: false,
                  color: '#0583FF'
                }}
                interactive={false}
              />
            )}
          </Pane>
          {/* Place boundary from PlaceContext geometry */}
          {placeBoundaryGeoJson && (
            <Pane name="PlaceBoundary" style={{ zIndex: 10002 }}>
              <GeoJSON
                data={placeBoundaryGeoJson as any}
                style={{
                  color: '#3B82F6',
                  weight: 2,
                  fillColor: '#93C5FD',
                  fillOpacity: 0.15
                }}
                interactive={false}
              />
            </Pane>
          )}
          <Pane name="RouteArrowMarkers" style={{ zIndex: 10010 }}>
            <RouteArrowMarkers
              routeCoords={changedRouteCoords}
              onRemoveMarker={removeMarker}
              onUpdateMarker={updateMarker}
              onBumpUpdated={() => setUpdated(uuidv4())}
              canDragMarkers={!isEditingLocked}
              canRemoveMarkers={!isEditingLocked}
              lockMirrorMarkers={isSymmetricalRoute}
              dropIndex={findDropIndex(changedRouteCoords)}
            />
          </Pane>
          <LandingPadsLayer landingZoneId={landingZoneId} />
          <Pane name="PolyRouteCoords" style={{ zIndex: 10003 }}>
            <Polyline
              positions={getOutboundRouteCoords(changedRouteCoords).map(point => point.coordinates)}
              color="white"
              interactive={false}
              key={`${landingZoneId}-routeCoords`}
            />
            <Polyline
              positions={getInboundRouteCoords(changedRouteCoords).map(point => point.coordinates)}
              color="white"
              dashArray="10, 10"
              interactive={false}
              key={`${landingZoneId}-routeCoords2`}
            />
            <Pane name="GeoJsonPane" style={{ zIndex: 10004 }}>
              {memoizedMissionRoutes &&
                Object.keys(memoizedMissionRoutes || {})?.map(missionId => {
                  return (
                    <GeoJSON
                      data={h3SetToFeature((memoizedMissionRoutes as any)[missionId])}
                      style={{
                        color: 'none',
                        fillOpacity: 0.5,
                        fillColor: order?.missionId === missionId ? 'yellow' : 'red'
                      }}
                      interactive={false}
                      key={`${missionId}`}
                    />
                  );
                })}
            </Pane>
          </Pane>
          <Pane
            key="Geofences"
            name="Geofences"
            style={{ zIndex: indexPriority(9) }}>
            {geofences &&
              geofences.length > 0 &&
              geofences
                ?.filter(
                  geofence =>
                    geofence.active === true
                )
                ?.map(geofence => {
                  return (
                    <GeoJSON
                      data={geofence.geoJson}
                      style={{ fill: false, color: geofenceColor(geofence.id) }}
                      interactive={false}
                      key={geofence.id}
                    />
                  );
                })}
          </Pane>
          <OrdersRoutesLayer orders={orders} orderStatuses={orderStatuses} indexPriority={indexPriority} />
          <Pane name="Obstacles" style={{ zIndex: 10005 }}>
            <Obstacles obstacles={obstacles} />
          </Pane>
          <Pane name="ElevationHazards" style={{ zIndex: 10006 }}>
            <ElevationHazards elevationHazards={elevationHazards} />
          </Pane>
        </MapContainer>
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, pointerEvents: 'none', zIndex: 100000 }}>
          {overlays}
        </div>

        {/* Floating right-side panels */}
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            width: 420,
            maxHeight: 'calc(100% - 24px)',
            overflowY: 'auto',
            pointerEvents: 'auto',
            zIndex: 100001
          }}
        >
          {/* Order details panel */}
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 10px 24px rgba(17,17,17,0.12)',
              border: '1px solid rgba(0,0,0,0.06)',
              padding: 12
            }}
          >
            {shortOrderId && (
              <Typography 
                variant="h5" 
                style={{ 
                  fontWeight: 600, 
                  fontSize: 20,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease',
                  userSelect: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,

                }}
                onClick={handleOrderIdClick}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {shortOrderId}
              </Typography>
            )}
            {/* Route panel */}
            <div
              style={{
                marginTop: 8,
                border: '1px solid #E5E7EB',
                borderRadius: 12,
                padding: 12
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16, background: '#F3F4F6', color: '#374151' }}>
                    <img src={confirmRouteIcon} alt="Route" style={{ width: 20, height: 20 }} />
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Route</div>
                </div>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    background: '#F3F4F6',
                    color: '#111827',
                    border: '1px solid #E5E7EB'
                  }}
                >
                  {order?.mission?.status || '—'}
                </span>
              </div>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #D1D5DB',
                      borderRadius: 6,
                      color: '#111827',
                      background: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={originLandingPad?.name || '—'}
                  >
                    {originLandingPad?.name || '—'}
                  </div>
                </div>
                <div style={{ width: 32, height: 32, borderRadius: 16, border: '1px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>➜</div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #D1D5DB',
                      borderRadius: 6,
                      color: '#111827',
                      background: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={order?.eircode || '—'}
                  >
                    {order?.eircode || '—'}
                  </div>
                </div>
                <div style={{ width: 32, height: 32, borderRadius: 16, border: '1px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>➜</div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: 14,
                      border: '1px solid #D1D5DB',
                      borderRadius: 6,
                      color: '#111827',
                      background: 'white',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={destinationLandingPad?.name || '—'}
                  >
                    {destinationLandingPad?.name || '—'}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 8, maxWidth: 360 }}>
              <InfoRow
                title="Mission ID"
                value={order?.missionId || '—'} 
                icon={<div style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>#</div>}
                containerStyle={{ marginBottom: 0 }}
              />
              <InfoRow
                title="Route Distance"
                value={renderOrderKm(routeDistanceKm)}
                icon={<div style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↔</div>}
                containerStyle={{ marginBottom: 0 }}
              />
            </div>
          </div>

          {/* Actions panel */}
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 10px 24px rgba(17,17,17,0.12)',
              border: '1px solid rgba(0,0,0,0.06)',
              padding: 12
            }}
          >
            <Typography variant="h5" style={{ fontWeight: 600, color: '#111827', marginTop: 8 }}>
              Actions
            </Typography>
            {editingLockedDueToMissingPreferredDrop && (
              <div style={{ marginTop: 12 }}>
                <Alert severity="warning">
                  Editing disabled: origin pad is set but no preferred drop location is configured for this place.
                </Alert>
                <div style={{ marginTop: 12 }} />
              </div>
            )}
            {renderAirspaceActions()}
          </div>


          <div
            style={{
              background: 'white',
              borderRadius: 12,
              boxShadow: '0 10px 24px rgba(17,17,17,0.12)',
              border: '1px solid rgba(0,0,0,0.06)',
              padding: 12
            }}
          >
            <Typography variant="h5" style={{ fontWeight: 600, color: '#111827', marginBottom: 8 }}>
              Altitude Profile
            </Typography>
            <div style={{ marginLeft: -20 }}>
              <RouteConfirmationGraph height={220} width={420} geoJson={geoJson} />
            </div>
          </div>

        </div>

      </div>
      {/* Delete Mission Airspace confirmation modal */}
      <ActionConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={callDeleteMissionAirspace}
        title={'Delete mission airspace'}
        description={'Are you sure you want to delete the current mission route? This action cannot be undone.'}
        actionText={isDeletingAirspace ? 'Deleting…' : 'Delete'}
      />
    </>
  );
};


